/**
 * ringMark — where the event actually sits on the ring.
 *
 * Lives in the rotating ring layer, so it turns with the ring and its position
 * is world coordinates. Its *size*, though, is screen pixels converted through
 * `ctx.screenScale`: a disc whose radius is fixed in world units is four pixels
 * across at fit zoom and eighty at 20x, which stops being a mark and starts
 * being a blob. Day ticks may scale; a mark has to stay a mark.
 *
 * Birthdays are discs, notes are diamonds — legible apart at five pixels.
 */
import { TAU, polarToXY } from '@circle/shared';
import type { Fragment, RingMarkCtx } from '../types';
import { n, svgEl } from '../../core/svg';
import { M, W } from './metrics';

/** Screen pixels. */
export const MARK_OFFSET = 11; // centre, outward from the ring line
const STEM = 9; // spur back toward the ring
const DOT = 4.6;
const DOT_HOT = 7;
const HALO = 6;
/** Widest the invisible target ever gets, in screen px. */
const TARGET_MAX = 15;
/** ...and the narrowest: an 10px disc, never smaller than the dot it covers. */
const TARGET_MIN = 5;

/**
 * Half-width of the invisible hit target, in screen px.
 *
 * A fixed 15px disc is right when the days are far apart and wrong when they
 * are not: at fit zoom consecutive days are under six pixels apart, so a run of
 * birthdays became one 30px-wide pile in which only the last-drawn mark could
 * ever be clicked. Capping the target at half the day pitch keeps neighbouring
 * targets from swallowing each other; `entryAt` in core/input.ts resolves what
 * overlap is left by nearest centre.
 */
export function markTargetRadius(screenScale: number, ringRadius: number = M.ring): number {
  const px = screenScale > 0 ? screenScale : 1;
  const pitchPx = (TAU * ringRadius) / (365 * px);
  return Math.min(TARGET_MAX, Math.max(TARGET_MIN, pitchPx / 2));
}

/**
 * How much prominence is allowed to change a mark's size (docs/CIRCLES.md §4).
 * A circle at 4 is louder, not four times the dot; a circle at 0.25 recedes
 * without becoming a speck you cannot aim at.
 */
export const SIZE_MIN = 0.7;
export const SIZE_MAX = 1.6;

export function markSizeFactor(prominence: number | undefined): number {
  const p = typeof prominence === 'number' && Number.isFinite(prominence) ? prominence : 1;
  return Math.min(SIZE_MAX, Math.max(SIZE_MIN, p));
}

export const ringMarkFragment: Fragment<RingMarkCtx> = (ctx, state) => {
  const hot = state.hover || state.selected;
  const colour = ctx.colour ?? (ctx.event.kind === 'note' ? 'var(--mark-note)' : 'var(--mark)');
  const orphaned = Boolean(ctx.event.orphanedAt);
  const px = ctx.screenScale > 0 ? ctx.screenScale : 1;
  const ring = ctx.radius > 0 ? ctx.radius : M.ring;
  const size = markSizeFactor(ctx.style?.prominence);

  const p = polarToXY(ctx.angle, ring + MARK_OFFSET * px);
  const spur = polarToXY(ctx.angle, ring + (MARK_OFFSET - STEM) * px);
  const r = (hot ? DOT_HOT : DOT) * px * size;
  const target = markTargetRadius(px, ring);

  const g = svgEl('g', {
    'data-frag': 'ringMark',
    opacity: orphaned ? 0.42 : 1,
  });

  // Invisible target: the visible dot is only a few pixels across. It grows to
  // 15px where the ring has room and shrinks to the day pitch where it has not.
  g.append(
    svgEl('circle', {
      cx: n(p.x),
      cy: n(p.y),
      r: n(target * px),
      fill: 'transparent',
      'pointer-events': 'fill',
    }),
  );

  g.append(
    svgEl('line', {
      x1: n(spur.x),
      y1: n(spur.y),
      x2: n(p.x),
      y2: n(p.y),
      stroke: colour,
      'stroke-width': hot ? W.ring : W.hairline,
      'vector-effect': 'non-scaling-stroke',
      opacity: hot ? 0.9 : 0.5,
      'pointer-events': 'none',
    }),
  );

  g.append(markShape(ctx.event.kind, p.x, p.y, r, colour, state.selected ? 1 : 0.92));

  if (hot) {
    g.append(
      svgEl('circle', {
        cx: n(p.x),
        cy: n(p.y),
        r: n(r + HALO * px),
        fill: 'none',
        stroke: colour,
        'stroke-width': W.hairline,
        'vector-effect': 'non-scaling-stroke',
        opacity: 0.75,
        'pointer-events': 'none',
      }),
    );
  }

  // Membership dots: on a merged ring an event is drawn once, in its loudest
  // circle's band, so the circles it is *also* in would otherwise go unsaid.
  // A row of pinheads just outside the mark, in each other circle's colour.
  const others = ctx.memberColours ?? [];
  if (others.length > 0) {
    const dotR = 1.9 * px;
    const step = 2.8 * dotR;
    const start = ring + (MARK_OFFSET + 8) * px + dotR;
    for (let i = 0; i < others.length; i++) {
      const at = polarToXY(ctx.angle, start + i * step);
      g.append(
        svgEl('circle', {
          cx: n(at.x),
          cy: n(at.y),
          r: n(dotR),
          fill: others[i]!,
          opacity: hot ? 0.95 : 0.7,
          'pointer-events': 'none',
        }),
      );
    }
  }

  return g;
};

function markShape(
  kind: 'birthday' | 'note',
  x: number,
  y: number,
  r: number,
  colour: string,
  opacity: number,
): SVGElement {
  if (kind === 'note') {
    const d = `M ${n(x)} ${n(y - r)} L ${n(x + r)} ${n(y)} L ${n(x)} ${n(y + r)} L ${n(x - r)} ${n(y)} Z`;
    return svgEl('path', { d, fill: colour, opacity, 'pointer-events': 'none' });
  }
  return svgEl('circle', {
    cx: n(x),
    cy: n(y),
    r: n(r),
    fill: colour,
    opacity,
    'pointer-events': 'none',
  });
}
